<template>
  <el-dialog
    @close="close()"
    :title="$t('ProjectSettings_SaveFeaturesDialog_Title')"
    :visible.sync="display"
    :modal-append-to-body="false"
    width="500px"
    center
  >
    <div class="center" v-if="hasChangedFeatures">
      <p>{{ $t('ProjectSettings_SaveFeaturesDialog_Message') }}</p>
      <div class="m-top-10">
        <ul>
          <li v-for="item in features">
            <template v-if="labels[item.name]">
              {{ labels[item.name] }} -
              <template v-if="valueLabels[item.name]">
                {{ $t(valueLabels[item.name][item.status]) }}
              </template>
              <template v-else>
                {{
                  (item.inverted
                  ? !item.status
                  : item.status)
                    ? $t('ProjectSettings_SaveFeaturesDialog_Enabled')
                    : $t('ProjectSettings_SaveFeaturesDialog_Disabled')
                }}
              </template>
            </template>
          </li>
        </ul>
      </div>
    </div>
    <div class="center" :class="{ 'm-top-30': features.length }">
      <p>{{ $t('ProjectSettings_SaveFeaturesDialog_Question') }}</p>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button size="small" type="primary" plain @click="close()">
        {{ $t('ProjectSettings_Button_Cancel') }}
      </el-button>
      <el-button size="small" type="primary" @click="save()">
        {{ $t('ProjectSettings_Button_Confirm') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { campaignThrottlingModeLabels } from '@/modules/project/enum/CampaignThrottlingModeEnum'

export default {
  name: 'SaveFeaturesDialog',
  props: {
    save: {
      type: Function,
      required: true
    },
    features: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      display: true,
      // features with more than enabled/disabled states
      valueLabels: {
        feature_campaign_throttling: campaignThrottlingModeLabels
      },
      labels: {
        push_encryption: this.$t('ProjectSettings_Field_Encryption'),
        push_delivery_receipts: this.$t(
          'ProjectSettings_Field_PushDeliveryReceipts'
        ),
        segmentation_events_use_profile: this.$t(
          'ProjectSettings_Field_SegmentationEventsUseProfile'
        ),
        profile_idfyby_email: this.$t(
          'ProjectSettings_Field_ProfileIdentifyByEmail'
        ),
        email_attachments: this.$t('ProjectSettings_Field_EmailAttachments'),
        profile_email_unique: this.$t(
          'ProjectSettings_Field_ProfileEmailUnique'
        ),
        sportradar: this.$t('ProjectSettings_Field_Sportradar'),
        maxautomation: this.$t('ProjectSettings_Field_Workflow'),
        content_import_export: this.$t(
          'ProjectSettings_Field_ContentImportExport'
        ),
        googleads: this.$t('ProjectSettings_Field_Googleads'),
        meta: this.$t('ProjectSettings_Field_Meta'),
        microsites: this.$t('ProjectSettings_Field_Microsites'),
        subscription_tracking: this.$t(
          'ProjectSettings_Field_SubscriptionTracking'
        ),
        subscription_preferences: this.$t(
          'ProjectSettings_Field_SubscriptionPreferences'
        ),
        campaign_engagement_delta_report: this.$t(
          'ProjectSettings_Field_CampaignEngagementDeltaReport'
        ),
        show_lithium_integration: this.$t(
          'ProjectSettings_Field_ShowLithiumIntegration'
        ),
        show_lithium_v2_integration: this.$t(
          'ProjectSettings_Field_ShowLithiumV2Integration'
        ),
        gig_integration: this.$t('ProjectSettings_Field_Gig_Integration'),
        audit_log_profile: this.$t('ProjectSettings_Field_AuditLog_Profile'),
        audit_log_campaign: this.$t('ProjectSettings_Field_AuditLog_Campaign'),
        feature_seed_list: this.$t('ProjectSettings_Field_Feature_SeedList'),
        disable_event_triggered_campaign_timeseries_segment: this.$t(
          'ProjectSettings_Field_Event_Triggered_Timeseries_Segments'
        ),
        disable_mobile_validation: this.$t(
          'ProjectSettings_Field_Disable_Mobile_Validation'
        ),
        inapp_native: this.$t('ProjectSettings_Field_Feature_InappNative'),
        feature_campaign_throttling: this.$t(
          'ProjectSettings_Field_Feature_CampaignThrottling'
        ),
        ai_text_generation: this.$t('ProjectSettings_Field_Ai'),
        campaign_templates: this.$t('ProjectSettings_Field_CampaignTemplates'),
        betsson_integration: this.$t(
          'ProjectSettings_Field_Betsson_Integration'
        ),
        show_open_bet_integration: this.$t(
          'ProjectSettings_Field_ShowOpenBetIntegration'
        ),
        allow_broadcast_for_multistage_timebased_campaigns: this.$t(
          'ProjectSettings_Field_Feature_AllowBroadcastForMultistageTimebasedCampaigns'
        ),
        rolling_segments: this.$t('ProjectSettings_Field_Rolling_Segments'),
        trino: this.$t('ProjectSettings_Field_Trino'),
        sampling: this.$t('ProjectSettings_Field_Sampling'),
        automation_campaign_execution: this.$t(
          'ProjectSettings_Field_Profile_Import_Campaign_Trigger'
        ),
        show_inbox_open_stats: this.$t(
          'ProjectSettings_Field_ShowInboxOpenStats'
        ),
        igt_integration: this.$t('ProjectSettings_Field_Igt_Integration'),
        opap_webhook_integration: this.$t(
          'ProjectSettings_Field_OPAPWebhookIntegration'
        ),
        allow_authorised_devices: this.$t(
          'ProjectSettings_Field_AllowAuthorisedDevices'
        ),
        workflow_clickmap: this.$t('ProjectSettings_Field_WorkflowClickmap'),
        save_as_report_campaign_start_time: this.$t(
          'ProjectSettings_Field_SaveAsReportCampaignStartTime'
        ),
        save_as_report_delta: this.$t(
          'ProjectSettings_Field_SaveAsReportDelta'
        ),
        allow_voucher_codes: this.$t('ProjectSettings_Field_VoucherCode'),
        oasis_integration: this.$t('ProjectSettings_Field_Oasis_Integration'),
        advanced_profile_search: this.$t(
          'ProjectSettings_Field_Advanced_Profile_Search'
        ),
        optimove_lists: this.$t('ProjectSettings_Field_Optimove_Lists'),
        campaign_content_preview: this.$t(
          'ProjectSettings_Field_Campaign_Content_Preview'
        ),
        action_aggregation_enabled: this.$t(
          'ProjectSettings_Field_Action_Message_Aggregation'
        ),
        no_changed_sub_tracking: this.$t(
          'ProjectSettings_Field_NoChanged_Subs'
        ),
        gateway_cost_tracking: this.$t(
          'ProjectSettings_Field_Feature_GatewayCostTracking'
        ),
        enable_new_goal_conversion: this.$t(
          'ProjectSettings_Field_EnableNewGoalConversion'
        ),
        enable_campaign_comparison_conversions: this.$t(
          'ProjectSettings_Field_EnableCampaignComparisonConversions'
        ),
        queue_email_msg_id_only: this.$t(
          'ProjectSettings_Field_QueueEmailMsgIdOnly'
        ),
        enable_email_last_open: this.$t(
          'ProjectSettings_Field_SegmentEmailLastOpened'
        ),
        preaggregated_clicked_links: this.$t(
          'ProjectSettings_Field_Feature_PreaggregatedClickedLinks'
        ),
        mxa_campaign_options: this.$t(
          'ProjectSettings_Field_Feature_MxaCampaignOptions'
        ),
        campaign_list_pagination_disable: this.$t(
          'ProjectSettings_Field_Feature_CampaignListPaginationDisable'
        ),
        allow_five_character_language_code: this.$t(
          'ProjectSettings_Field_Feature_AllowFiveCharacterLanguageCode'
        ),
        ios_live_activities: this.$t(
          'ProjectSettings_Field_Ios_Live_Activities'
        ),
        open_search_enabled: this.$t(
          'ProjectSettings_Field_OpenSearch_Integration'
        ),
        new_mxa: this.$t('ProjectSettings_Field_New_Mxa'),
        mxa_migration_tool: this.$t('ProjectSettings_Field_Mxa_Migration_Tool'),
        campaign_trigger_by_consent_pref_events: this.$t(
          'ProjectSettings_Field_CampaignTriggerByConsentPrefEvents'
        ),
        rum_tracking: this.$t('ProjectSettings_Field_Rum_Tracking'),
        mxa_event_node: this.$t('ProjectSettings_Field_Mxa_Event_Node'),
        no_changed_attribute_handling: this.$t(
          'ProjectSettings_Field_NoChanged_Attribute_Handling'
        ),
        warply_integration: this.$t(
          'ProjectSettings_Field_ShowWarplyIntegration'
        ),
        webhook_stream_integration: this.$t('Webhook_Stream_Integration_Label'),
        living_segments: this.$t('Living_Segments_Label'),
        auto_generate_email: this.$t('ProjectSettings_Field_AutoGenerateEmail'),
        auto_generate_mobile: this.$t(
          'ProjectSettings_Field_AutoGenerateMobile'
        ),
        profile_importer_type: this.$t(
          'ProjectSettings_Field_Batch_Profile_Import'
        ),
        disable_kafka_stream_event_filtering: this.$t(
          'ProjectSettings_Field_Disable_Kafka_Stream_Event_Filtering'
        ),
        content_fetch: this.$t('ProjectSettings_Field_Content_Fetch'),
        content_fetch_retry: this.$t(
          'ProjectSettings_Field_Content_Fetch_Retry'
        ),
        customer_id_enabled: this.$t(
          'ProjectSettings_Field_Customer_Id_Enabled'
        ),
        tag_categories: this.$t('ProjectSettings_Field_TagCategories'),
        open_live_activities: this.$t(
          'ProjectSettings_Field_Open_Live_Activities'
        ),
        open_live_activities_batch_size: this.$t(
          'ProjectSettings_Field_Open_Live_Activities_Batch_Size'
        ),
        stricter_tag_validation: this.$t(
          'ProjectSettings_Field_StricterTagValidation'
        ),
        legacy_mxa_enabled: this.$t('Campaign_Dialog_OldMxa'),
        comeon_integration: this.$t('Campaign_Dialog_ComeOn'),
        tag_operator_validation: this.$t(
          'ProjectSettings_Field_TagOperatorValidation'
        ),
        apptoken_enabled: this.$t('ProjectSettings_Field_ApptokenEnabled'),
        profile_import_handle_id_conflicts: this.$t(
          'ProjectSettings_Field_ProfileImportHandleIdConflicts'
        ),
        profile_require_user_id: this.$t(
          'ProjectSettings_Field_ProfileRequireUserId'
        ),
        sms_usage_reporting: this.$t('ProjectFeatures_Field_SmsUsageReporting'),
        kafka_sport_feed_enabled: this.$t(
          'ProjectFeatures_Field_KafkaSportFeed'
        )
      }
    }
  },
  computed: {
    hasChangedFeatures() {
      if (!this.features.length) {
        return false
      }

      return this.features.filter(feature => this.labels[feature.name]).length
    }
  },
  methods: {
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
>>> .el-dialog__footer {
  margin-top: -20px;
}

.center {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/project/components/SaveFeaturesDialog.vue